/**
 * DramaShorts Theme — JavaScript
 * Handles header interactions, search, mobile menu, video cards, etc.
 */
(function () {
  'use strict';

  // ============================================================
  // DOM Ready
  // ============================================================
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  ready(function () {
    initHeader();
    initMobileMenu();
    initSearchOverlay();
    initVideoCards();
    initProductGallery();
    initCartQuantity();
    initVariantSwitcher();
    initPasswordForm();
    initNewsletterForm();
  });

  // ============================================================
  // Sticky Header scroll effect
  // ============================================================
  function initHeader() {
    var header = document.getElementById('siteHeader');
    if (!header) return;

    var lastScroll = 0;

    window.addEventListener('scroll', function () {
      var scrollY = window.pageYOffset;

      if (scrollY > 80) {
        header.style.boxShadow = '0 2px 20px rgba(0,0,0,0.3)';
      } else {
        header.style.boxShadow = 'none';
      }

      lastScroll = scrollY;
    }, { passive: true });
  }

  // ============================================================
  // Mobile Menu
  // ============================================================
  function initMobileMenu() {
    var toggle = document.getElementById('mobileMenuToggle');
    var menu = document.getElementById('mobileMenu');
    var backdrop = document.getElementById('mobileMenuBackdrop');
    var close = document.getElementById('mobileMenuClose');

    if (!toggle || !menu || !backdrop) return;

    function openMenu() {
      menu.classList.add('is-open');
      backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', openMenu);
    if (close) close.addEventListener('click', closeMenu);
    backdrop.addEventListener('click', closeMenu);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });
  }

  // ============================================================
  // Search Overlay
  // ============================================================
  function initSearchOverlay() {
    var toggle = document.getElementById('searchToggle');
    var overlay = document.getElementById('searchOverlay');
    var close = document.getElementById('searchClose');
    var input = document.getElementById('searchInput');

    if (!toggle || !overlay) return;

    function openSearch() {
      overlay.classList.add('is-open');
      if (input) {
        setTimeout(function () { input.focus(); }, 200);
      }
      document.body.style.overflow = 'hidden';
    }

    function closeSearch() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', openSearch);
    if (close) close.addEventListener('click', closeSearch);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
        closeSearch();
      }
    });
  }

  // ============================================================
  // Video Card hover effects — lazy load, play preview on hover
  // ============================================================
  function initVideoCards() {
    // Intersection Observer for lazy loading
    if ('IntersectionObserver' in window) {
      var imageObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            imageObserver.unobserve(img);
          }
        });
      }, { rootMargin: '200px' });

      document.querySelectorAll('.video-card__image[data-src]').forEach(function (img) {
        imageObserver.observe(img);
      });
    }

    // Card entrance animation
    var cards = document.querySelectorAll('.video-card:not(.video-card--placeholder)');
    if ('IntersectionObserver' in window) {
      var cardObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            cardObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      cards.forEach(function (card, index) {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        card.style.transition = 'opacity 0.5s ease, transform 0.5s ease ' + (index * 0.05) + 's';
        cardObserver.observe(card);
      });
    }
  }

  // ============================================================
  // Product Gallery (thumbnails)
  // ============================================================
  function initProductGallery() {
    var thumbs = document.querySelectorAll('.product__thumb');
    var mediaItems = document.querySelectorAll('.product__media-item');

    if (!thumbs.length || !mediaItems.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var mediaId = this.dataset.mediaId;

        // Update active thumb
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');

        // Show corresponding media
        mediaItems.forEach(function (item) {
          if (item.dataset.mediaId === mediaId) {
            item.style.display = 'block';
            // Auto-play video if switching to video media
            var video = item.querySelector('video');
            if (video) video.play();
          } else {
            item.style.display = 'none';
            var video = item.querySelector('video');
            if (video) video.pause();
          }
        });
      });
    });

    // Initialize: show only first media item
    if (mediaItems.length > 1) {
      mediaItems.forEach(function (item, index) {
        if (index === 0) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
        }
      });
    }
  }

  // ============================================================
  // Cart Quantity
  // ============================================================
  function initCartQuantity() {
    var qtyInputs = document.querySelectorAll('.cart-item__qty-input--small');

    qtyInputs.forEach(function (input) {
      var defaultValue = input.value;

      input.addEventListener('change', function () {
        var key = this.dataset.key;
        var qty = parseInt(this.value, 10);

        if (isNaN(qty) || qty < 0) {
          this.value = defaultValue;
          return;
        }

        // Submit quantity change via fetch
        var body = new FormData();
        body.append('updates[' + key + ']', qty);

        fetch(window.DramaShorts.routes.cartUpdate, {
          method: 'POST',
          body: body,
        }).then(function (response) {
          if (response.ok) {
            // Reload to reflect changes
            window.location.reload();
          }
        }).catch(function () {
          input.value = defaultValue;
        });
      });
    });
  }

  // ============================================================
  // Variant Switcher
  // ============================================================
  function initVariantSwitcher() {
    var variantRadios = document.querySelectorAll('.product__variant-option input[type="radio"]');

    variantRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        // Find the selected variant
        var selectedOptions = {};
        document.querySelectorAll('.product__variant-option input:checked').forEach(function (input) {
          selectedOptions[input.name] = input.value;
        });

        // Find matching variant from product JSON
        var productJson = document.querySelector('[data-product-json]');
        if (!productJson) return;

        try {
          var product = JSON.parse(productJson.textContent);
          var matchingVariant = product.variants.find(function (variant) {
            return variant.options.every(function (option, index) {
              return option === selectedOptions[product.options[index].name];
            });
          });

          if (matchingVariant) {
            // Update hidden variant input
            var variantInput = document.querySelector('input[name="id"]');
            if (variantInput) variantInput.value = matchingVariant.id;

            // Update price display
            var priceCurrent = document.querySelector('.product__price-current');
            var priceCompare = document.querySelector('.product__price-compare');
            var priceBadge = document.querySelector('.product__price-badge');
            var addBtn = document.querySelector('.product__add-btn');

            if (priceCurrent) {
              priceCurrent.textContent = formatMoney(matchingVariant.price, window.DramaShorts.moneyFormat);
            }
            if (priceCompare) {
              if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
                priceCompare.textContent = formatMoney(matchingVariant.compare_at_price, window.DramaShorts.moneyFormat);
                priceCompare.style.display = '';
              } else {
                priceCompare.style.display = 'none';
              }
            }
            if (priceBadge) {
              if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
                var saved = matchingVariant.compare_at_price - matchingVariant.price;
                priceBadge.textContent = 'Save ' + formatMoney(saved, window.DramaShorts.moneyFormat);
                priceBadge.style.display = '';
              } else {
                priceBadge.style.display = 'none';
              }
            }
            if (addBtn) {
              if (matchingVariant.available) {
                addBtn.disabled = false;
                addBtn.textContent = 'Add to Cart';
              } else {
                addBtn.disabled = true;
                addBtn.textContent = 'Unavailable';
              }
            }

            // Update featured media
            if (matchingVariant.featured_media) {
              var thumbToClick = document.querySelector('[data-media-id="' + matchingVariant.featured_media.id + '"]');
              if (thumbToClick) thumbToClick.click();
            }
          }
        } catch (e) {
          console.error('Variant switch error:', e);
        }
      });
    });
  }

  // Simple money formatting helper
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    var value = (cents / 100).toFixed(2);
    // Very basic: just prepend the currency symbol
    if (format && format.indexOf('{{') === -1) {
      return format.replace('amount', value);
    }
    return '$' + value;
  }

  // ============================================================
  // Password page
  // ============================================================
  function initPasswordForm() {
    var form = document.querySelector('.password-form');
    if (!form) return;

    form.addEventListener('submit', function () {
      var btn = form.querySelector('.password-btn');
      if (btn) {
        btn.textContent = 'Verifying...';
        btn.disabled = true;
      }
    });
  }

  // ============================================================
  // Newsletter form
  // ============================================================
  function initNewsletterForm() {
    var form = document.querySelector('.newsletter__form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      var input = form.querySelector('.newsletter__input');
      if (!input || !input.value.trim()) {
        e.preventDefault();
        input.focus();
      }
    });
  }

  // ============================================================
  // Keyboard navigation
  // ============================================================
  document.addEventListener('keydown', function (e) {
    // Ctrl+K or / to open search
    if ((e.ctrlKey && e.key === 'k') || (e.key === '/' && document.activeElement === document.body)) {
      e.preventDefault();
      var searchToggle = document.getElementById('searchToggle');
      if (searchToggle) searchToggle.click();
    }
  });

})();
